import { useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { Document, ProcessingStatus } from '@/types/Document';
import { API_URL, FASTAPI_URL } from '@/lib/config';
import { updateDocumentProcessingResults, updateDocumentType } from '@/lib/api-client';

interface UseDocumentOperationsProps {
  token: string | undefined;
  onDocumentsChange: () => Promise<void>;
  onStatusUpdate: (documentId: string, status: ProcessingStatus) => void;
  onJobIdUpdate: (documentId: string, jobId: string) => void;
  startPolling: (documentId: string, jobId: string) => void;
  docTypeSelections: Record<string, string>;
  onAuthError: () => void;
}

export function useDocumentOperations({
  token,
  onDocumentsChange,
  onStatusUpdate,
  onJobIdUpdate,
  startPolling,
  docTypeSelections,
  onAuthError
}: UseDocumentOperationsProps) {
  const [processingDocId, setProcessingDocId] = useState<string | null>(null);
  const [error, setError] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  const clearMessages = useCallback(() => {
    setError('');
    setMessage('');
  }, []);

  const handleFileUpload = useCallback(async (files: FileList) => {
    if (!files || files.length === 0) return;

    if (!token) {
      setError('User not authenticated');
      return;
    }

    setError('');
    const uploadErrors: string[] = [];
    const uploadPromises = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const formData = new FormData();
      formData.append('file', file);
      formData.append('convertToPdf', 'true');

      const uploadEndpoint = `${API_URL}/documents/upload-with-conversion`;

      uploadPromises.push(
        fetch(uploadEndpoint, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
          body: formData,
        })
        .then(response => {
          if (!response.ok) {
            return response.text().then(text => {
              let detail = 'Failed to upload document';
              try {
                const jsonError = JSON.parse(text);
                detail = jsonError.detail || detail;
              } catch (e) {
                // Ignore json parsing error
              }
              throw new Error(`${file.name}: ${detail} (Status: ${response.status})`);
            });
          }
          return response.json();
        })
        .catch(err => {
          uploadErrors.push(err instanceof Error ? err.message : `Error uploading ${file.name}`);
        })
      );
    }

    await Promise.all(uploadPromises);
    await onDocumentsChange();

    if (uploadErrors.length > 0) {
      setError(`Some uploads failed:\n${uploadErrors.join('\n')}`);
    } else {
      const docxCount = Array.from(files).filter(file => 
        file.name.toLowerCase().endsWith('.docx') || file.name.toLowerCase().endsWith('.doc')
      ).length;
      
      let successMessage = `${files.length} document(s) uploaded successfully.`;
      if (docxCount > 0) {
        successMessage += ` ${docxCount} DOCX file(s) have been automatically converted to PDF.`;
      }
      
      setMessage(successMessage);
      setTimeout(() => setMessage(''), 5000);
    }

    return uploadErrors.length === 0;
  }, [token, onDocumentsChange]);

  const handleDownload = useCallback(async (documentId: string, filename: string) => {
    try {
      if (!token) {
        throw new Error('User not authenticated');
      }
      
      const response = await fetch(`${API_URL}/documents/${documentId}/content`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error downloading document');
    }
  }, [token]);

  const handleDelete = useCallback(async (documentId: string) => {
    try {
      if (!token) {
        throw new Error('User not authenticated');
      }
      
      const response = await fetch(`${API_URL}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete document');
      }

      await onDocumentsChange();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error deleting document');
    }
  }, [token, onDocumentsChange]);

  const handleProcess = useCallback(async (document: Document) => {
    try {
      if (!token) {
        throw new Error('User not authenticated');
      }
      
      const documentId = document.id;
      setProcessingDocId(documentId);
      onStatusUpdate(documentId, 'preparing');
      
      try {
        await updateDocumentProcessingResults(documentId, { status: 'preparing' });
      } catch (updateErr) {
        console.error('Error updating document status to preparing:', updateErr);
        if (updateErr instanceof Error && updateErr.message.includes('authentication')) {
          throw updateErr;
        }
      }
      
      // Download the file content
      const response = await fetch(`${API_URL}/documents/${documentId}/content`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to download document for processing');
      }
      
      const blob = await response.blob();
      const file = new File([blob], document.originalName, { type: blob.type });
      
      // Upload to FastAPI
      onStatusUpdate(documentId, 'uploading');
      
      try {
        await updateDocumentProcessingResults(documentId, { status: 'uploading' });
      } catch (updateErr) {
        console.error('Error updating document status to uploading:', updateErr);
      }
      
      const formData = new FormData();
      formData.append('file', file);
      const selectedDocType = docTypeSelections[documentId] || 'procedure';
      formData.append('document_type', selectedDocType);
      
      const uploadResponse = await fetch(`${FASTAPI_URL}/process`, {
        method: 'POST',
        body: formData,
      });
      
      if (!uploadResponse.ok) {
        throw new Error('Failed to upload document to processing server');
      }
      
      const uploadData = await uploadResponse.json();
      const jobId = uploadData.job_id;
      
      onJobIdUpdate(documentId, jobId);
      onStatusUpdate(documentId, uploadData.status);
      
      try {
        await updateDocumentProcessingResults(documentId, { 
          status: uploadData.status,
          jobId: jobId
        });
      } catch (updateErr) {
        console.error('Error updating document status and job ID:', updateErr);
      }
      
      setMessage('Document processing started successfully');
      setTimeout(() => setMessage(''), 5000);
      
      startPolling(documentId, jobId);
      
    } catch (err) {
      Sentry.captureException(err, {
        tags: { flow: 'document-processing', stage: 'orchestration' },
        extra: { documentId: document.id },
      });
      console.error('Error processing document:', err);
      
      if (err instanceof Error && err.message.includes('authentication')) {
        setError('Authentication error: Please log in again');
        onAuthError();
        return;
      }
      
      setError(err instanceof Error ? err.message : 'Error processing document');
      onStatusUpdate(document.id, 'error');
      
      try {
        await updateDocumentProcessingResults(document.id, { status: 'error' });
      } catch (updateErr) {
        console.error('Error updating document status to error:', updateErr);
      }
      
      setTimeout(() => setError(''), 5000);
    } finally {
      setProcessingDocId(null);
    }
  }, [token, docTypeSelections, onStatusUpdate, onJobIdUpdate, startPolling, onAuthError]);

  const handleDocTypeChange = useCallback(async (documentId: string, typeId: string) => {
    try {
      await updateDocumentType(documentId, typeId);
    } catch (err) {
      console.error("Failed to save document type:", err);
      setError('Failed to update document type. Please try again.');
      setTimeout(() => setError(''), 3000);
      throw err; // Re-throw to let the component handle reverting
    }
  }, []);

  return {
    processingDocId,
    error,
    message,
    clearMessages,
    handleFileUpload,
    handleDownload,
    handleDelete,
    handleProcess,
    handleDocTypeChange
  };
}
